"use client";

import { AvatarCat } from "@bice_vida/ds-components-avatar-cat";
import { Input, InputControl, InputLabel, InputField, InputMessage } from "@bice_vida/ds-components-input";
import { useRegisterStepProgress } from "@front-account/components/common/action-buttons/step-progress-context";
import { useRecoveryFlowConfig } from "@front-account/journeys/recovery/use-recovery-flow-config";
import { sendPasswordRecovery } from "@front-account/services/federation/mutations/recovery";
import { getRecoveryFlowConfig } from "@front-account/services/federation/queries/transactions";
import { getUserCanRecoverPassword } from "@front-account/services/federation/queries/user";
import { usePersistingRecoveryStore, useRecoveryStore } from "@front-account/stores/recovery";
import { env } from "@front-account/utils/libs/env";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { formatRUT } from "@login/utils/format/rut";
import { getSegmentedRut } from "@login/utils/parse/rut";
import { useMutation } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { useRouter, useSearchParams } from "next/navigation";
import Script from "next/script";
import { useTranslations } from "next-intl";
import { useEffect, useState, useTransition } from "react";
import { useForm, Controller } from "react-hook-form";

import { ModalResult, type ResultStatusT } from "../modal-result";

import styles from "./recovery-form.module.scss";
import { RECOVERY_SCHEMA, type RecoveryFormShapeT } from "./recovery-form.validation";

const HCaptcha = dynamic(() => import("@hcaptcha/react-hcaptcha").then((mod) => mod.default), {
  ssr: false,
  loading: () => <div className={styles.captchaSkeleton} aria-hidden="true" />,
});

export function RecoveryForm() {
  const t = useTranslations("recovery");
  const [resultStatus, setResultStatus] = useState<ResultStatusT | undefined>(undefined);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [captchaError, setCaptchaError] = useState(false);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  const setRut = useRecoveryStore((s) => s.setRut);
  const setPersistingRut = usePersistingRecoveryStore((s) => s.setRut);
  const setIsOtpDataUpdate = usePersistingRecoveryStore((s) => s.setIsOtpDataUpdate);
  const setAccountData = useRecoveryStore((s) => s.setAccountData);
  const setUseNewRecoveryFlow = useRecoveryStore((s) => s.setUseNewRecoveryFlow);
  const setResultOpen = useRecoveryStore((s) => s.setResultOpen);
  const router = useRouter();
  const [isNavigating, startNavigation] = useTransition();
  const { useNewRecoveryFlow } = useRecoveryFlowConfig();

  const searchParams = useSearchParams();

  const userRut = searchParams.get("rut");

  const defaultRut = formatRutFromUrl(userRut);

  useEffect(() => {
    router.prefetch("/recuperar-cuenta/elegir-canal");
  }, [router]);

  const siteKey = env.NEXT_PUBLIC_HCAPTCHA_SITEKEY;
  const isCaptchaEnabled = Boolean(siteKey);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<RecoveryFormShapeT>({
    resolver: valibotResolver(RECOVERY_SCHEMA),
    defaultValues: {
      user: defaultRut,
    },
  });

  const { mutateAsync: sendPasswordRecoveryMutate, isPending: isSendPasswordRecoveryPending } = useMutation({
    mutationFn: sendPasswordRecovery,
  });
  const { mutateAsync: getUserCanRecoverPasswordMutate, isPending: isGetUserCanRecoverPasswordPending } = useMutation({
    mutationFn: getUserCanRecoverPassword,
  });

  const isPending = isSendPasswordRecoveryPending || isGetUserCanRecoverPasswordPending;

  const handlePasswordRecovery = async ({ user }: RecoveryFormShapeT) => {
    if (isCaptchaEnabled && !captchaToken) {
      setCaptchaError(true);
      return;
    }
    setCaptchaError(false);
    const numericRut = +getSegmentedRut(user).rut;
    try {
      const {
        getUserCanRecoverPassword: { recoveryFlow, email },
      } = await getUserCanRecoverPasswordMutate({ rut: numericRut, captchaToken: captchaToken ?? undefined });
      setRut(numericRut);
      setPersistingRut(numericRut);
      setIsOtpDataUpdate(false);
      setAccountData({ email: "", phone: "" });
      setUserEmail(email ?? undefined);

      if (recoveryFlow === "password") {
        const recoveryFlowConfig =
          useNewRecoveryFlow !== undefined
            ? useNewRecoveryFlow
            : (await getRecoveryFlowConfig()).getRecoveryFlowConfig.useNewRecoveryFlow;

        setUseNewRecoveryFlow(recoveryFlowConfig);

        if (recoveryFlowConfig) {
          startNavigation(() => {
            router.push("/recuperar-cuenta/elegir-canal");
          });
          return;
        }

        await sendPasswordRecoveryMutate({ rut: user });
        setResultStatus("HAS_MAIL");
      } else if (recoveryFlow === "account") {
        setResultStatus("NO_MAIL");
      } else {
        setResultStatus("NO_PRODUCTS");
      }
    } catch {
      setRut(undefined);
      setUseNewRecoveryFlow(undefined);
      setResultStatus("ERROR");
      if (isCaptchaEnabled) {
        setCaptchaToken(null);
        setCaptchaKey((k) => k + 1);
      }
    } finally {
      setResultOpen(true);
    }
  };

  useRegisterStepProgress({
    onContinue: handleSubmit(handlePasswordRecovery),
    disabled: !!Object.keys(errors).length,
    loading: isPending || isNavigating,
  });

  function formatRutFromUrl(strRut: string | null): string {
    if (!strRut || !/^\d+$/.test(strRut)) return "";

    const { rut: baseRut, dv } = getSegmentedRut(strRut);
    return formatRUT(`${baseRut}${dv}`);
  }

  return (
    <>
      {isCaptchaEnabled && (
        <Script src="https://js.hcaptcha.com/1/api.js?render=explicit" strategy="afterInteractive" />
      )}

      <div className={styles.container}>
        <div className={styles.welcomeContainer}>
          <AvatarCat avatar="jacket2" size="xl" />
        </div>

        <h2 className={styles.title}>{t("TITLE")}</h2>

        <form name="recovery" className={styles.form} onSubmit={handleSubmit(handlePasswordRecovery)}>
          <Controller
            name="user"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input className={styles.input} status={errors.user ? "error" : isValid ? "success" : undefined}>
                <InputControl>
                  <InputLabel>{t("FORM.INPUTS.USER.NAME")}</InputLabel>

                  <InputField
                    value={value}
                    onChange={(e) => {
                      let value = e.target.value;

                      if (/^\d/.test(e.target.value)) {
                        value = formatRUT(e.target.value);
                      }

                      onChange(value);
                    }}
                  />
                </InputControl>

                {errors.user && <InputMessage>{t(errors.user?.message ?? "")}</InputMessage>}
              </Input>
            )}
          />

          {isCaptchaEnabled && siteKey && (
            <div className={styles.captchaWrapper}>
              <div className={styles.warpHCaptcha}>
                <HCaptcha
                  key={captchaKey}
                  sitekey={siteKey}
                  onVerify={(token) => {
                    setCaptchaToken(token);
                    setCaptchaError(false);
                  }}
                  onExpire={() => setCaptchaToken(null)}
                  languageOverride="es"
                />

                {captchaError && <span className={styles.captchaError}>{t("RESULT.ERROR.CAPTCHA_REQUIRED")}</span>}
              </div>
            </div>
          )}
        </form>
      </div>

      {resultStatus && <ModalResult userEmail={userEmail} status={resultStatus} />}
    </>
  );
}
